import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ChatMessageCollection, GroupChatCollection, NotificationCollection, TypingStatusCollection } from '../interface/collection';

declare const Meteor: any;

export interface ChatFileAttachment {
  _id?: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  fileData: string;
  uploadedAt?: Date;
}

export interface PollOption {
  id: string;
  text: string;
  votes: number;
  voters: string[];
}

export interface Poll {
  question: string;
  options: PollOption[];
  allowMultiple: boolean;
  totalVotes: number;
  createdAt: Date;
  expiresAt?: Date;
}

export interface ChatMessage {
  _id?: string;
  id?: string;
  text: string;
  sender: 'user' | 'assistant';
  userId: string;
  userName?: string;
  userAvatar?: string;
  recipientId?: string;
  groupId?: string; // for group conversations
  conversationId?: string;
  attachment?: ChatFileAttachment;
  poll?: Poll;
  sharedContact?: Contact;
  edited?: boolean;
  editedAt?: Date;
  timestamp: Date;
  createdAt?: Date;
  isTyping?: boolean;
  read?: boolean;
}

export interface Contact {
  userId: string;
  name: string;
  avatar: string;
  email: string;
  lastMessageTime?: Date;
  unreadCount?: number;
  isGroup?: boolean; // true if this is a group chat
  groupId?: string; // group chat ID if isGroup is true
  participants?: Array<{ userId: string; userName?: string; userAvatar?: string }>; // for group chats
}

export interface GroupChatParticipant {
  userId: string;
  userName?: string;
  userAvatar?: string;
  userEmail?: string;
  role: 'admin' | 'member';
  joinedAt: Date;
}

export interface GroupChat {
  _id?: string;
  name: string;
  description?: string;
  createdBy: string;
  createdByName?: string;
  createdByAvatar?: string;
  participants: GroupChatParticipant[];
  avatar?: string;
  createdAt: Date;
  updatedAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService implements OnDestroy {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  private userId: string = '';
  private userName: string = '';
  private userAvatar: string = '';
  private destroy$ = new Subject<void>();
  private subscriptionHandle: any = null;
  private autorunHandle: any = null;
  private notificationSubscriptionHandle: any = null;
  private notificationAutorunHandle: any = null;
  private notificationSubject = new BehaviorSubject<any>(null);
  public notification$: Observable<any> = this.notificationSubject.asObservable();

  private typingStatusSubscriptionHandle: any = null;
  private typingStatusAutorunHandle: any = null;
  private typingStatusSubject = new BehaviorSubject<any>(null);
  public typingStatus$: Observable<any> = this.typingStatusSubject.asObservable();
  private typingTimeout: any = null;

  constructor() {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribe();
  }

  /**
   * Set the current user and initialize chat
   */
  setUser(userId: string, userName?: string, userAvatar?: string): void {
    this.userId = userId;
    this.userName = userName || 'User';
    this.userAvatar = userAvatar || '';

    if (this.userId) {
      this.subscribeToMessages();
    }
  }

  /**
   * Subscribe to chat messages via Meteor pub/sub
   */
  private subscribeToMessages(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    // Unsubscribe from previous subscription if exists
    this.unsubscribe();

    // Subscribe to chat messages for this user
    this.subscriptionHandle = Meteor.subscribe('chatMessages', this.userId, {
      onReady: () => {
        console.log('Chat messages subscription ready');
        this.loadMessages();
      },
      onError: (error: any) => {
        console.error('Error subscribing to chat messages:', error);
      }
    });

    // Set up reactive autorun to watch for changes
    this.autorunHandle = Meteor.autorun(() => {
      if (this.subscriptionHandle?.ready()) {
        this.loadMessages();
      }
    });

    // Subscribe to notifications
    this.subscribeToNotifications();

    // Subscribe to typing status
    this.subscribeToTypingStatus();
  }

  /**
   * Subscribe to notifications via Meteor pub/sub
   */
  private subscribeToNotifications(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    // Unsubscribe from previous notification subscription if exists
    if (this.notificationSubscriptionHandle) {
      this.notificationSubscriptionHandle.stop();
    }
    if (this.notificationAutorunHandle) {
      this.notificationAutorunHandle.stop();
    }

    // Subscribe to notifications for this user
    this.notificationSubscriptionHandle = Meteor.subscribe('notifications', this.userId, {
      onReady: () => {
        console.log('Notifications subscription ready');
        this.loadNotifications();
      },
      onError: (error: any) => {
        console.error('Error subscribing to notifications:', error);
      }
    });

    // Set up reactive autorun to watch for new notifications
    this.notificationAutorunHandle = Meteor.autorun(() => {
      if (this.notificationSubscriptionHandle?.ready()) {
        this.loadNotifications();
      }
    });
  }

  /**
   * Load notifications from Meteor collection and emit new ones
   */
  private loadNotifications(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    try {
      const notifications = NotificationCollection.find(
        { userId: this.userId, read: false },
        { sort: { createdAt: -1 } }
      ).fetch();

      // Emit the most recent unread notification
      if (notifications.length > 0) {
        const latestNotification = notifications[0];
        this.notificationSubject.next(latestNotification);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }

  /**
   * Load messages from Meteor collection (all conversations user is part of)
   */
  private loadMessages(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    try {
      const userGroups = GroupChatCollection.find({ 'participants.userId': this.userId }).fetch();
      const groupIds = (userGroups || []).map((g: any) => g._id).filter(Boolean);
      const query: any = {
        $or: [
          { userId: this.userId },
          { recipientId: this.userId }
        ]
      };
      if (groupIds.length > 0) {
        query.$or.push({ groupId: { $in: groupIds } });
      }
      const meteorMessages = ChatMessageCollection.find(query, { sort: { createdAt: 1 } }).fetch();

      const messages: ChatMessage[] = meteorMessages.map((msg: any) => ({
        _id: msg._id,
        id: msg._id,
        text: msg.text,
        sender: msg.userId === this.userId ? 'user' : 'assistant',
        userId: msg.userId,
        userName: msg.userName,
        userAvatar: msg.userAvatar,
        recipientId: msg.recipientId,
        groupId: msg.groupId,
        conversationId: msg.conversationId,
        attachment: msg.attachment,
        poll: msg.poll,
        sharedContact: msg.sharedContact,
        edited: msg.edited || false,
        editedAt: msg.editedAt ? new Date(msg.editedAt) : undefined,
        timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
        createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date(),
        read: msg.read || false,
        isTyping: false
      }));

      this.messagesSubject.next(messages);
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  }

  /**
   * Get current messages
   */
  getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  /**
   * Send a message via Meteor method
   */
  sendMessage(text: string): void {
    if (!text.trim() || !this.userId || typeof Meteor === 'undefined') {
      return;
    }

    const message = {
      text: text.trim(),
      sender: 'user' as const,
      userId: this.userId,
      userName: this.userName,
      userAvatar: this.userAvatar,
      createdAt: new Date()
    };

    Meteor.call('addChatMessage', message, (error: any, result: any) => {
      if (error) {
        console.error('Error sending chat message:', error);
      } else if (result?.code !== 200) {
        console.error('Failed to send message:', result?.message);
      }
      // Message will be added via reactive subscription
    });
  }

  /**
   * Add assistant response (called by server or AI service)
   */
  addAssistantResponse(text: string): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    const message = {
      text: text.trim(),
      sender: 'assistant' as const,
      userId: this.userId,
      userName: 'Assistant',
      createdAt: new Date()
    };

    Meteor.call('addChatMessage', message, (error: any, result: any) => {
      if (error) {
        console.error('Error adding assistant response:', error);
      }
    });
  }

  /**
   * Add typing indicator (client-side only)
   */
  addTypingIndicator(): void {
    const typingMessage: ChatMessage = {
      id: 'typing-indicator',
      text: '',
      sender: 'assistant',
      userId: this.userId,
      timestamp: new Date(),
      isTyping: true
    };

    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, typingMessage]);
  }

  /**
   * Remove typing indicator
   */
  removeTypingIndicator(): void {
    const currentMessages = this.messagesSubject.value;
    const filtered = currentMessages.filter(msg => msg.id !== 'typing-indicator');
    this.messagesSubject.next(filtered);
  }

  /**
   * Clear all messages for current user
   */
  clearMessages(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    Meteor.call('clearChatMessages', this.userId, (error: any, result: any) => {
      if (error) {
        console.error('Error clearing chat messages:', error);
      } else if (result?.code === 200) {
        // Add welcome message
        this.addAssistantResponse('Hello! How can I help you today?');
      }
    });
  }

  /**
   * Subscribe to typing status
   */
  private subscribeToTypingStatus(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    // Unsubscribe from previous typing status subscription if exists
    if (this.typingStatusSubscriptionHandle) {
      this.typingStatusSubscriptionHandle.stop();
    }
    if (this.typingStatusAutorunHandle) {
      this.typingStatusAutorunHandle.stop();
    }

    // Subscribe to typing status for this user
    this.typingStatusSubscriptionHandle = Meteor.subscribe('typingStatus', this.userId, {
      onReady: () => {
        console.log('Typing status subscription ready');
        this.loadTypingStatus();
      },
      onError: (error: any) => {
        console.error('Error subscribing to typing status:', error);
      }
    });

    // Set up reactive autorun to watch for typing status changes
    this.typingStatusAutorunHandle = Meteor.autorun(() => {
      if (this.typingStatusSubscriptionHandle?.ready()) {
        this.loadTypingStatus();
      }
    });
  }

  /**
   * Load typing status from Meteor collection
   */
  private loadTypingStatus(): void {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    try {
      const typingStatuses = TypingStatusCollection.find(
        { recipientId: this.userId, isTyping: true }
      ).fetch();

      // Emit the most recent typing status
      if (typingStatuses.length > 0) {
        const latest = typingStatuses[0];
        // Check if status is recent (within last 3 seconds)
        const timeDiff = Date.now() - new Date(latest.updatedAt).getTime();
        if (timeDiff <= 3000) {
          this.typingStatusSubject.next(latest);
        } else {
          this.typingStatusSubject.next(null);
        }
      } else {
        this.typingStatusSubject.next(null);
      }
    } catch (error) {
      console.error('Error loading typing status:', error);
    }
  }

  /**
   * Set typing status for current user
   */
  setTypingStatus(recipientId: string, isTyping: boolean): void {
    if (!this.userId || typeof Meteor === 'undefined' || !recipientId) {
      return;
    }

    // Clear existing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    Meteor.call('setTypingStatus', this.userId, this.userName, recipientId, isTyping, (error: any) => {
      if (error) {
        console.error('Error setting typing status:', error);
      }
    });

    // Auto-clear typing status after 3 seconds if still typing
    if (isTyping) {
      this.typingTimeout = setTimeout(() => {
        this.setTypingStatus(recipientId, false);
      }, 3000);
    }
  }

  /**
   * Unsubscribe from Meteor subscription
   */
  private unsubscribe(): void {
    if (this.subscriptionHandle) {
      this.subscriptionHandle.stop();
      this.subscriptionHandle = null;
    }
    if (this.autorunHandle) {
      this.autorunHandle.stop();
      this.autorunHandle = null;
    }
    if (this.notificationSubscriptionHandle) {
      this.notificationSubscriptionHandle.stop();
      this.notificationSubscriptionHandle = null;
    }
    if (this.notificationAutorunHandle) {
      this.notificationAutorunHandle.stop();
      this.notificationAutorunHandle = null;
    }
    if (this.typingStatusSubscriptionHandle) {
      this.typingStatusSubscriptionHandle.stop();
      this.typingStatusSubscriptionHandle = null;
    }
    if (this.typingStatusAutorunHandle) {
      this.typingStatusAutorunHandle.stop();
      this.typingStatusAutorunHandle = null;
    }
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
  }

  /**
   * Cancel a connection request (by sender)
   */
  async cancelConnectionRequest(requestId: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('cancelConnectionRequest', requestId, this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error cancelling connection request:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Delete a received connection request (by recipient)
   */
  async deleteReceivedRequest(requestId: string, action: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('deleteReceivedRequest', requestId, this.userId, action, (error: any, result: any) => {
        if (error) {
          console.error('Error deleting received connection request:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return;
    }

    Meteor.call('markNotificationAsRead', notificationId, this.userId, (error: any) => {
      if (error) {
        console.error('Error marking notification as read:', error);
      }
    });
  }

  /**
   * Get current user info
   */
  getCurrentUser(): { userId: string; userName: string; userAvatar: string } {
    return {
      userId: this.userId,
      userName: this.userName,
      userAvatar: this.userAvatar
    };
  }

  /**
   * Search for users
   * @param searchQuery Search string (min 2 chars)
   * @param currentUserId Optional; uses service user if not provided
   */
  async searchUsers(searchQuery: string, currentUserId?: string): Promise<any[]> {
    const userId = currentUserId ?? this.userId;
    if (!userId) {
      return [];
    }

    return new Promise((resolve, reject) => {
      Meteor.call('searchUsers', searchQuery, userId, (error: any, result: any) => {
        if (error) {
          console.error('Error searching users:', error);
          reject(error);
        } else if (result?.code === 200) {
          resolve(result.data || []);
        } else {
          resolve([]);
        }
      });
    });
  }

  /**
   * Send a connection request
   */
  async sendConnectionRequest(toUserId: string, message?: string): Promise<any> {
    if (!this.userId) {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call(
        'sendConnectionRequest',
        this.userId,
        this.userName,
        this.userAvatar,
        toUserId,
        message,
        (error: any, result: any) => {
          if (error) {
            console.error('Error sending connection request:', error);
            reject(error);
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  /**
   * Get connection requests
   */
  async getConnectionRequests(): Promise<any[]> {
    if (!this.userId) {
      return [];
    }

    return new Promise((resolve, reject) => {
      Meteor.call('getConnectionRequests', this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error getting connection requests:', error);
          reject(error);
        } else if (result?.code === 200) {
          resolve(result.data || []);
        } else {
          resolve([]);
        }
      });
    });
  }

  /**
   * Respond to a connection request
   */
  async respondToConnectionRequest(requestId: string, action: 'accepted' | 'rejected'): Promise<any> {
    if (!this.userId) {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('respondToConnectionRequest', requestId, this.userId, action, (error: any, result: any) => {
        if (error) {
          console.error('Error responding to connection request:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Get accepted connections (contacts)
   */
  async getAcceptedConnections(): Promise<Contact[]> {
    if (!this.userId) {
      return [];
    }

    return new Promise((resolve, reject) => {
      Meteor.call('getAcceptedConnections', this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error getting accepted connections:', error);
          reject(error);
        } else if (result?.code === 200) {
          resolve(result.data || []);
        } else {
          resolve([]);
        }
      });
    });
  }

  /**
   * Send a message to a specific user
   */
  async sendMessageToUser(toUserId: string, text: string, attachment?: ChatFileAttachment): Promise<any> {
    if (!this.userId) {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call(
        'sendMessageToUser',
        this.userId,
        this.userName,
        this.userAvatar,
        toUserId,
        text,
        attachment,
        (error: any, result: any) => {
          if (error) {
            console.error('Error sending message to user:', error);
            reject(error);
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  /**
   * Upload a file for chat (max 2MB)
   */
  async uploadChatFile(file: File): Promise<ChatFileAttachment | null> {
    if (!this.userId) {
      return null;
    }

    // Check file size (2MB limit)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size exceeds 2MB limit');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const fileData = e.target.result; // base64 data URI

        Meteor.call(
          'uploadChatFile',
          this.userId,
          file.name,
          fileData,
          file.type,
          file.size,
          (error: any, result: any) => {
            if (error) {
              console.error('Error uploading file:', error);
              reject(error);
            } else if (result?.code === 200) {
              resolve(result.data);
            } else {
              reject(new Error(result?.message || 'Failed to upload file'));
            }
          }
        );
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Get conversation messages between two users
   */
  async getConversationMessages(otherUserId: string): Promise<ChatMessage[]> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return [];
    }

    return new Promise((resolve, reject) => {
      Meteor.call('getConversationMessages', this.userId, otherUserId, (error: any, result: any) => {
        if (error) {
          console.error('Error getting conversation messages:', error);
          reject(error);
        } else if (result?.code === 200) {
          const messages: ChatMessage[] = (result.data || []).map((msg: any) => ({
            _id: msg._id,
            id: msg._id,
            text: msg.text,
            sender: msg.userId === this.userId ? 'user' : 'assistant',
            userId: msg.userId,
            userName: msg.userName,
            userAvatar: msg.userAvatar,
            recipientId: msg.recipientId,
            conversationId: msg.conversationId,
            attachment: msg.attachment,
            poll: msg.poll,
            edited: msg.edited || false,
            editedAt: msg.editedAt ? new Date(msg.editedAt) : undefined,
            timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
            createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date(),
            read: msg.read || false,
            isTyping: false
          }));
          resolve(messages);
        } else {
          resolve([]);
        }
      });
    });
  }

  /**
   * Block a connection request
   */
  async blockConnectionRequest(requestId: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('blockConnectionRequest', requestId, this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error blocking connection request:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Update/edit a chat message
   */
  async updateChatMessage(messageId: string, newText: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('updateChatMessage', messageId, this.userId, newText, (error: any, result: any) => {
        if (error) {
          console.error('Error updating message:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Delete a chat message
   */
  async deleteChatMessage(messageId: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('deleteChatMessage', messageId, this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error deleting message:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Mark messages as read for a conversation
   */
  async markMessagesAsRead(otherUserId: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('markMessagesAsRead', this.userId, otherUserId, (error: any, result: any) => {
        if (error) {
          console.error('Error marking messages as read:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Get common phrases for autocomplete
   */
  async getCommonPhrases(): Promise<string[]> {
    if (typeof Meteor === 'undefined') {
      return this.getDefaultPhrases();
    }

    return new Promise((resolve) => {
      Meteor.call('getCommonPhrases', (error: any, result: any) => {
        if (error) {
          console.error('Error fetching common phrases:', error);
          resolve(this.getDefaultPhrases());
        } else if (result?.code === 200 && result.data) {
          resolve(result.data);
        } else {
          resolve(this.getDefaultPhrases());
        }
      });
    });
  }

  /**
   * Get default phrases as fallback
   */
  private getDefaultPhrases(): string[] {
    return [
      'How are you?',
      'Thank you!',
      'You\'re welcome',
      'See you later',
      'Have a great day!',
      'Good morning',
      'Good afternoon',
      'Good evening',
      'Nice to meet you',
      'How can I help?',
      'That sounds good',
      'I agree',
      'Let me know',
      'Talk to you soon',
      'Take care'
    ];
  }

  /**
   * Delete a connection (unfriend)
   */
  async deleteConnection(requestId: string, action: 'pending' | 'accepted' | 'rejected' | 'blocked' | 'cancelled'): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('deleteConnection', requestId, this.userId, action, (error: any, result: any) => {
        if (error) {
          console.error('Error deleting connection:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Clear conversation messages between two users
   */
  async clearConversationMessages(userId1: string, userId2: string): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('clearConversationMessages', userId1, userId2, (error: any, result: any) => {
        if (error) {
          console.error('Error clearing conversation messages:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Create a new group chat
   */
  async createGroupChat(name: string, participantUserIds: string[]): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('createGroupChat', name, this.userId, this.userName, this.userAvatar, participantUserIds, (error: any, result: any) => {
        if (error) {
          console.error('Error creating group chat:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Get all group chats for current user
   */
  async getGroupChats(): Promise<GroupChat[]> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return [];
    }

    return new Promise((resolve, reject) => {
      Meteor.call('getGroupChats', this.userId, (error: any, result: any) => {
        if (error) {
          console.error('Error getting group chats:', error);
          reject(error);
        } else {
          resolve(result?.data || []);
        }
      });
    });
  }

  /**
   * Update a group chat (name and/or participants)
   */
  async updateGroupChat(groupId: string, name?: string, participantUserIds?: string[]): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined' || !groupId) {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('updateGroupChat', groupId, this.userId, name, participantUserIds, (error: any, result: any) => {
        if (error) {
          console.error('Error updating group chat:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Send a message to a group chat
   */
  async sendMessageToGroup(groupId: string, text: string, attachment?: any): Promise<any> {
    if (!this.userId || !groupId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('sendMessageToGroup', this.userId, this.userName, this.userAvatar, groupId, text, attachment, (error: any, result: any) => {
        if (error) {
          console.error('Error sending group message:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Get messages for a group chat
   */
  async getGroupChatMessages(groupId: string): Promise<ChatMessage[]> {
    if (!groupId || typeof Meteor === 'undefined') {
      return [];
    }

    return new Promise((resolve, reject) => {
      Meteor.call('getGroupChatMessages', groupId, (error: any, result: any) => {
        if (error) {
          console.error('Error getting group messages:', error);
          reject(error);
        } else {
          const messages: ChatMessage[] = (result?.data || []).map((msg: any) => ({
            _id: msg._id,
            id: msg._id,
            text: msg.text,
            sender: msg.userId === this.userId ? 'user' : 'assistant',
            userId: msg.userId,
            userName: msg.userName,
            userAvatar: msg.userAvatar,
            groupId: msg.groupId,
            conversationId: msg.conversationId,
            attachment: msg.attachment,
            poll: msg.poll,
            edited: msg.edited || false,
            editedAt: msg.editedAt ? new Date(msg.editedAt) : undefined,
            timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
            createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date(),
            read: msg.read || false,
            isTyping: false
          }));
          resolve(messages);
        }
      });
    });
  }

  /**
   * Add participants to a group chat
   */
  async addGroupChatParticipants(groupId: string, participantUserIds: string[]): Promise<any> {
    if (!this.userId || !groupId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('addGroupChatParticipants', groupId, this.userId, participantUserIds, (error: any, result: any) => {
        if (error) {
          console.error('Error adding participants:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Remove a participant from a group chat
   */
  async removeGroupChatParticipant(groupId: string, participantUserId: string): Promise<any> {
    if (!this.userId || !groupId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('removeGroupChatParticipant', groupId, this.userId, participantUserId, (error: any, result: any) => {
        if (error) {
          console.error('Error removing participant:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Create a poll message
   */
  async createPollMessage(toUserId: string | null, groupId: string | null, question: string, options: string[], allowMultiple: boolean = false, expiresAt?: Date): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('createPollMessage', this.userId, this.userName, this.userAvatar, toUserId, groupId, question, options, allowMultiple, expiresAt, (error: any, result: any) => {
        if (error) {
          console.error('Error creating poll:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Share a contact
   */
  async shareContact(toUserId: string | null, groupId: string | null, contact: Contact): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('shareContact', this.userId, this.userName, this.userAvatar, toUserId, groupId, contact, (error: any, result: any) => {
        if (error) {
          console.error('Error sharing contact:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Vote on a poll
   */
  async voteOnPoll(messageId: string, optionIds: string[]): Promise<any> {
    if (!this.userId || typeof Meteor === 'undefined') {
      return null;
    }

    return new Promise((resolve, reject) => {
      Meteor.call('voteOnPoll', messageId, this.userId, optionIds, (error: any, result: any) => {
        if (error) {
          console.error('Error voting on poll:', error);
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  }
}

